import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import StarRating from './StarRating';
import UpgradeModal from './UpgradeModal';
import SpamAlertModal from './SpamAlertModal';
import './BookDetail.css';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

// Helper function to process **text** and make it bold
const processBoldText = (text) => {
  const parts = [];
  let lastIndex = 0;
  const regex = /\*\*(.+?)\*\*/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    // Add the bold text
    parts.push(<strong key={match.index}>{match[1]}</strong>);
    lastIndex = regex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

const BookDetail = () => {
  const { id } = useParams();
  const { user, refreshUser } = useAuth();
  const [book, setBook] = useState(null);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [error, setError] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [hasUserSummary, setHasUserSummary] = useState(false);
  const [question, setQuestion] = useState('');
  const [questionAnswer, setQuestionAnswer] = useState('');
  const [submittingQuestion, setSubmittingQuestion] = useState(false);
  const [savedQuestions, setSavedQuestions] = useState([]);
  const [showSpamAlert, setShowSpamAlert] = useState(false);
  const [questionsPaused, setQuestionsPaused] = useState(false);
  const [summaryJustGenerated, setSummaryJustGenerated] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [studyGuide, setStudyGuide] = useState(null);
  const [generatingGuide, setGeneratingGuide] = useState(false);
  const [emailingGuide, setEmailingGuide] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [showStudyGuideToast, setShowStudyGuideToast] = useState(false);

  useEffect(() => {
    fetchBookDetails();
    fetchSummaries(); // Auto-load summaries if user has already generated one
    fetchStudyGuide(); // Auto-load discussion guide if user has already generated one
    checkIfBookIsSaved(); // Check if book is saved
    loadSavedQuestions();
    checkQuestionsPaused();
    setSummaryJustGenerated(false);
    setShowToast(false);
    setShowStudyGuideToast(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  // Removed - don't fetch summaries on user change

  const fetchBookDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/books/${id}`);
      setBook(response.data.book);
    } catch (err) {
      setError('Failed to load book details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummaries = async () => {
    try {
      const response = await axios.get(`${API_URL}/summaries/book/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const summariesData = response.data.summaries || [];
      setSummaries(summariesData);
      
      // Check if current user has requested a summary for this book
      if (user && summariesData.length > 0) {
        setHasUserSummary(true);
      } else {
        setHasUserSummary(false);
      }
    } catch (err) {
      console.error('Failed to fetch summaries:', err);
      setSummaries([]);
      setHasUserSummary(false);
    }
  };

  const fetchStudyGuide = async () => {
    try {
      const response = await axios.get(`${API_URL}/study-guides/book/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setStudyGuide(response.data.studyGuide);
    } catch (err) {
      console.error('Failed to fetch discussion guide:', err);
      setStudyGuide(null);
    }
  };

  const checkIfBookIsSaved = async () => {
    if (!user) {
      setIsSaved(false);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/books/saved/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const savedBooks = response.data.books || [];
      const isBookSaved = savedBooks.some(book => book.google_books_id === id);
      setIsSaved(isBookSaved);
    } catch (err) {
      console.error('Failed to check if book is saved:', err);
      setIsSaved(false);
    }
  };

  const handleSaveBook = async () => {
    setSaving(true);
    try {
      if (isSaved) {
        await axios.delete(`${API_URL}/books/${id}/save`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        setIsSaved(false);
      } else {
        await axios.post(`${API_URL}/books/${id}/save`, {}, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        setIsSaved(true);
      }
    } catch (err) {
      setError('Failed to save book');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateSummary = async () => {
    // Check if user already has requested a summary for this book
    if (hasUserSummary) {
      setError('You have already requested a summary for this book.');
      return;
    }

    // Check summary count - allow 10 free summaries
    const summaryCount = getSummaryCount();
    if (summaryCount >= 10 && !user?.is_premium) {
      setError('');
      setShowUpgradeModal(true);
      return;
    }

    // Check if user is premium (for unlimited summaries)
    if (!user?.is_premium && summaryCount >= 10) {
      setError('');
      setShowUpgradeModal(true);
      return;
    }

    setGenerating(true);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/summaries/create`, {
        bookId: id,
        bookTitle: book?.title,
        bookDescription: book?.description,
        bookAuthors: book?.authors
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      // Fetch summaries after successful generation to show the new one
      await fetchSummaries();
      setHasUserSummary(true);
      setSummaryJustGenerated(true);
      incrementSummaryCount();
      
      // Show toast notification
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
      }, 5000); // Hide after 5 seconds
    } catch (err) {
      // Check if user already has requested a summary
      if (err.response?.status === 400 && 
          err.response?.data?.error?.toLowerCase().includes('already')) {
        setError('You have already requested a summary for this book.');
        setHasUserSummary(true);
        // If the existing summary is returned, fetch summaries to show it
        if (err.response.data.summary) {
          await fetchSummaries();
        }
      } else if (err.response?.status === 403 || 
          err.response?.data?.error?.toLowerCase().includes('premium') ||
          err.response?.data?.error?.toLowerCase().includes('subscription') ||
          err.response?.data?.error?.toLowerCase().includes('upgrade')) {
        setError('');
        setShowUpgradeModal(true);
      } else {
        setError(err.response?.data?.error || 'Failed to generate summary. Make sure OpenAI API key is configured.');
      }
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleUpgrade = () => {
    const url = window.location.href.toLowerCase();
    const hasAISparkNotes = url.includes('aisparknotes');
    
    if (hasAISparkNotes) {
      // Redirect to Stripe checkout for AISparkNotes
      window.location.href = 'https://buy.stripe.com/00wfZi5Eo9bF3BfaeC0Ba0d';
    } else {
      // Default behavior for BookSummary
      setShowUpgradeModal(false);
      window.location.href = 'booksummary://upgrade';
    }
  };

  const handleGenerateStudyGuide = async () => {
    // Check if discussion guide already exists
    if (studyGuide) {
      setError('You have already generated a discussion guide for this book.');
      return;
    }

    // Check discussion guide count - allow 1 free discussion guide
    const guideCount = getDiscussionGuideCount();
    if (guideCount >= 1 && !user?.is_premium) {
      setError('');
      setShowUpgradeModal(true);
      return;
    }

    // Check if user is premium (for unlimited discussion guides)
    if (!user?.is_premium && guideCount >= 1) {
      setError('');
      setShowUpgradeModal(true);
      return;
    }

    setGeneratingGuide(true);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/study-guides/create`, {
        bookId: id,
        bookTitle: book?.title,
        bookDescription: book?.description,
        bookAuthors: book?.authors
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setStudyGuide(response.data.studyGuide);
      incrementDiscussionGuideCount();
      
      // Show toast notification
      setShowStudyGuideToast(true);
      setTimeout(() => {
        setShowStudyGuideToast(false);
      }, 5000); // Hide after 5 seconds
    } catch (err) {
      if (err.response?.status === 403 || 
          err.response?.data?.error?.toLowerCase().includes('premium') ||
          err.response?.data?.error?.toLowerCase().includes('subscription') ||
          err.response?.data?.error?.toLowerCase().includes('upgrade')) {
        setError('');
        setShowUpgradeModal(true);
      } else {
        setError(err.response?.data?.error || 'Failed to generate discussion guide. Make sure OpenAI API key is configured.');
      }
      console.error(err);
    } finally {
      setGeneratingGuide(false);
    }
  };

  const handleEmailStudyGuide = async () => {
    if (!studyGuide) {
      setError('No discussion guide available to email.');
      return;
    }

    setEmailingGuide(true);
    setError('');
    setEmailSuccess(false);
    try {
      await axios.post(`${API_URL}/study-guides/book/${id}/email`, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setEmailSuccess(true);
      setTimeout(() => {
        setEmailSuccess(false);
      }, 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to email discussion guide. Please try again.');
      console.error(err);
    } finally {
      setEmailingGuide(false);
    }
  };

  const loadSavedQuestions = () => {
    try {
      const key = `book_questions_${id}`;
      const saved = localStorage.getItem(key);
      if (saved) {
        setSavedQuestions(JSON.parse(saved));
      } else {
        setSavedQuestions([]);
      }
    } catch (err) {
      console.error('Error loading saved questions:', err);
      setSavedQuestions([]);
    }
  };

  const saveQuestion = (questionText, answer) => {
    try {
      const key = `book_questions_${id}`;
      const newQuestion = {
        question: questionText,
        answer: answer,
        timestamp: new Date().toISOString()
      };
      const updated = [newQuestion, ...savedQuestions];
      setSavedQuestions(updated);
      localStorage.setItem(key, JSON.stringify(updated));
    } catch (err) {
      console.error('Error saving question:', err);
    }
  };

  // Check if question is spam (not relevant to books)
  const isSpamQuestion = (questionText, bookTitle) => {
    const question = questionText.toLowerCase().trim();
    const title = bookTitle?.toLowerCase() || '';
    
    // Spam indicators
    const spamPatterns = [
      /^(hi|hello|hey|test|testing|asdf|123|abc)/i,
      /^(what|who|where|when|why|how)\s+(is|are|was|were)\s+(this|that|it|you|your|the|a|an)\s*[?]?$/i,
      /^(lol|haha|hahaha|hehe|hehehe)/i,
      /^[^a-z]*$/i, // Only special characters/numbers
      /^.{0,5}$/i, // Too short
      /(spam|test|testing|fake|dummy|placeholder)/i
    ];

    // Check if question mentions book-related terms
    const bookTerms = ['book', 'author', 'character', 'plot', 'story', 'theme', 'chapter', 'page', title.split(' ')[0]];
    const hasBookContext = bookTerms.some(term => term && question.includes(term.toLowerCase()));

    // If it matches spam patterns and has no book context, it's likely spam
    const matchesSpamPattern = spamPatterns.some(pattern => pattern.test(question));
    
    return matchesSpamPattern && !hasBookContext && question.length < 20;
  };

  // Get question count for today for this book
  const getTodayQuestionCount = () => {
    try {
      const key = `book_questions_count_${id}`;
      const data = localStorage.getItem(key);
      if (!data) return 0;
      
      const { date, count } = JSON.parse(data);
      const today = new Date().toDateString();
      
      if (date === today) {
        return count;
      }
      return 0;
    } catch (err) {
      return 0;
    }
  };

  // Increment question count for today
  const incrementQuestionCount = () => {
    try {
      const key = `book_questions_count_${id}`;
      const today = new Date().toDateString();
      const currentCount = getTodayQuestionCount();
      
      localStorage.setItem(key, JSON.stringify({
        date: today,
        count: currentCount + 1
      }));
    } catch (err) {
      console.error('Error incrementing question count:', err);
    }
  };

  // Get spam count
  const getSpamCount = () => {
    try {
      const data = localStorage.getItem('spam_question_count');
      if (!data) return 0;
      
      const { date, count } = JSON.parse(data);
      const today = new Date().toDateString();
      
      if (date === today) {
        return count;
      }
      return 0;
    } catch (err) {
      return 0;
    }
  };

  // Increment spam count
  const incrementSpamCount = () => {
    try {
      const today = new Date().toDateString();
      const currentCount = getSpamCount();
      const newCount = currentCount + 1;
      
      localStorage.setItem('spam_question_count', JSON.stringify({
        date: today,
        count: newCount
      }));

      if (newCount >= 3) {
        // Pause questions for 24 hours
        const pauseUntil = new Date();
        pauseUntil.setDate(pauseUntil.getDate() + 1);
        localStorage.setItem('questions_paused_until', pauseUntil.toISOString());
        setQuestionsPaused(true);
        setShowSpamAlert(true);
      }
    } catch (err) {
      console.error('Error incrementing spam count:', err);
    }
  };

  // Check if questions are paused
  const checkQuestionsPaused = () => {
    try {
      const pausedUntil = localStorage.getItem('questions_paused_until');
      if (!pausedUntil) {
        setQuestionsPaused(false);
        return;
      }

      const pauseDate = new Date(pausedUntil);
      const now = new Date();

      if (now > pauseDate) {
        // Pause expired, clear it
        localStorage.removeItem('questions_paused_until');
        localStorage.removeItem('spam_question_count');
        setQuestionsPaused(false);
      } else {
        setQuestionsPaused(true);
      }
    } catch (err) {
      setQuestionsPaused(false);
    }
  };

  // Get summary count
  const getSummaryCount = () => {
    try {
      const data = localStorage.getItem('summary_count');
      if (!data) return 0;
      return parseInt(data, 10) || 0;
    } catch (err) {
      return 0;
    }
  };

  // Increment summary count
  const incrementSummaryCount = () => {
    try {
      const currentCount = getSummaryCount();
      localStorage.setItem('summary_count', (currentCount + 1).toString());
    } catch (err) {
      console.error('Error incrementing summary count:', err);
    }
  };

  // Get discussion guide count
  const getDiscussionGuideCount = () => {
    try {
      const data = localStorage.getItem('discussion_guide_count');
      if (!data) return 0;
      return parseInt(data, 10) || 0;
    } catch (err) {
      return 0;
    }
  };

  // Increment discussion guide count
  const incrementDiscussionGuideCount = () => {
    try {
      const currentCount = getDiscussionGuideCount();
      localStorage.setItem('discussion_guide_count', (currentCount + 1).toString());
    } catch (err) {
      console.error('Error incrementing discussion guide count:', err);
    }
  };

  const handleAskQuestion = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;

    // Check if questions are paused
    if (questionsPaused) {
      setError('Your ability to ask questions has been paused for 24 hours due to invalid questions.');
      return;
    }

    // Check question count for non-premium users (3 per day per book)
    if (!user?.is_premium) {
      const questionCount = getTodayQuestionCount();
      if (questionCount >= 3) {
        setError('');
        setShowUpgradeModal(true);
        return;
      }
    }

    // Check for spam
    const isSpam = isSpamQuestion(question, book?.title);
    if (isSpam) {
      incrementSpamCount();
      setError('Please ask a question relevant to the book.');
      setQuestion('');
      return;
    }

    setSubmittingQuestion(true);
    setQuestionAnswer('');
    setError('');

    try {
      const response = await axios.post(
        `${API_URL}/books/${id}/question`,
        { question: question.trim() },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      const answer = response.data.answer;
      setQuestionAnswer(answer);
      saveQuestion(question.trim(), answer);
      incrementQuestionCount();
      setQuestion(''); // Clear the question input after successful submission
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to get answer. Please try again.');
      console.error(err);
    } finally {
      setSubmittingQuestion(false);
    }
  };


  if (loading) {
    return <div className="loading">Loading book details...</div>;
  }

  if (!book) {
    return <div className="container"><div className="error">Book not found</div></div>;
  }

  return (
    <div className="container">
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleUpgrade}
      />
      <SpamAlertModal 
        isOpen={showSpamAlert} 
        onClose={() => setShowSpamAlert(false)}
      />
      {showToast && (
        <div className="toast-notification">
          <div className="toast-content">
            <span className="toast-icon">✓</span>
            <span className="toast-message">Summary generated! Scroll down to view it.</span>
            <button 
              className="toast-close"
              onClick={() => setShowToast(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}
      {showStudyGuideToast && (
        <div className="toast-notification">
          <div className="toast-content">
            <span className="toast-icon">✓</span>
            <span className="toast-message">Discussion guide generated! Scroll down to view it.</span>
            <button 
              className="toast-close"
              onClick={() => setShowStudyGuideToast(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}
      <div className="book-detail">
        <div className="book-detail-header">
          <div className="book-cover">
            {book.image_url ? (
              <img src={book.image_url} alt={book.title} />
            ) : (
              <div className="no-cover">No Cover</div>
            )}
          </div>
          <div className="book-info">
            <h1>{book.title}</h1>
            <p className="book-authors">
              {book.authors?.join(', ') || 'Unknown Author'}
            </p>
            {book.published_date && (
              <p className="book-meta">Published: {book.published_date}</p>
            )}
            {book.page_count && (
              <p className="book-meta">Pages: {book.page_count}</p>
            )}
            {book.categories && book.categories.length > 0 && (
              <div className="book-categories">
                {book.categories.map((cat, idx) => (
                  <span key={idx} className="category-tag">{cat}</span>
                ))}
              </div>
            )}
            <div className="book-actions">
              <button
                onClick={handleSaveBook}
                className={`btn ${isSaved ? 'btn-secondary' : 'btn-primary'}`}
                disabled={saving}
              >
                {saving ? '...' : isSaved ? 'Saved' : 'Save Book'}
              </button>
              <button
                onClick={handleGenerateSummary}
                className="btn btn-primary"
                disabled={generating || hasUserSummary}
              >
                {generating ? 'Generating Summary...' : summaryJustGenerated ? 'Summary Generated' : hasUserSummary ? 'Summary Already Generated' : 'Generate AI Summary'}
              </button>
              <button
                onClick={handleGenerateStudyGuide}
                className="btn btn-primary"
                disabled={generatingGuide || studyGuide}
              >
                {generatingGuide ? 'Generating Discussion Guide...' : studyGuide ? 'Discussion Guide Generated' : 'Generate Discussion Guide'}
              </button>
            </div>
          </div>
        </div>

        {error && !showUpgradeModal && <div className="error">{error}</div>}

        <div className="card">
          <h2>Ask Questions...</h2>
          <form onSubmit={handleAskQuestion} className="question-form">
            <div className="input-group">
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Ask a question about this book..."
                className="question-input"
                rows="4"
                disabled={submittingQuestion}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submittingQuestion || !question.trim() || questionsPaused}
            >
              {submittingQuestion ? 'Asking...' : questionsPaused ? 'Questions Paused' : 'Ask Question'}
            </button>
            {!user?.is_premium && (
              <p className="question-limit-info">
                {3 - getTodayQuestionCount()} questions remaining for this book today
              </p>
            )}
          </form>
          {questionAnswer && (
            <div className="question-answer">
              <h3>Answer:</h3>
              <p>{questionAnswer}</p>
            </div>
          )}
          
          {savedQuestions.length > 0 && (
            <div className="saved-questions">
              <h3>Previous Questions</h3>
              <div className="questions-accordion">
                {savedQuestions.map((item, index) => (
                  <QuestionAccordionItem key={index} question={item.question} answer={item.answer} timestamp={item.timestamp} />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <h2>Description</h2>
          {book.description && book.description.trim() ? (
            <div className="book-description-text">
              {book.description
                .replace(/<br\s*\/?>/gi, '\n') // Convert <br> tags to newlines first
                .replace(/<\/p>/gi, '\n\n') // Convert </p> to double newlines
                .replace(/<p[^>]*>/gi, '') // Remove <p> tags
                .replace(/<[^>]*>/g, '') // Remove remaining HTML tags
                .replace(/&nbsp;/g, ' ') // Replace &nbsp; with spaces
                .replace(/&amp;/g, '&') // Replace &amp; with &
                .replace(/&lt;/g, '<') // Replace &lt; with <
                .replace(/&gt;/g, '>') // Replace &gt; with >
                .replace(/&quot;/g, '"') // Replace &quot; with "
                .replace(/&#39;/g, "'") // Replace &#39; with '
                .replace(/&apos;/g, "'") // Replace &apos; with '
                .replace(/&#8217;/g, "'") // Replace &#8217; with '
                .replace(/&#8220;/g, '"') // Replace &#8220; with "
                .replace(/&#8221;/g, '"') // Replace &#8221; with "
                .replace(/&mdash;/g, '—') // Replace &mdash; with em dash
                .replace(/&ndash;/g, '–') // Replace &ndash; with en dash
                .trim()}
            </div>
          ) : (
            <p className="no-description" style={{ color: '#666', fontStyle: 'italic' }}>
              No description available for this book.
            </p>
          )}
        </div>

        <div className="card">
          <h2>Summary</h2>
          {summaries.length === 0 ? (
            <p>No summaries yet. Be the first to generate one!</p>
          ) : (
            summaries.map((summary) => (
              <SummaryCard key={summary.id} summary={summary} onRatingChange={fetchSummaries} />
            ))
          )}
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0 }}>Discussion Guide</h2>
            {studyGuide && (
              <button
                onClick={handleEmailStudyGuide}
                className="btn btn-secondary"
                disabled={emailingGuide}
                style={{ marginLeft: '1rem' }}
              >
                {emailingGuide ? 'Sending...' : emailSuccess ? '✓ Emailed!' : '📧 Email Discussion Guide'}
              </button>
            )}
          </div>
          {emailSuccess && (
            <div className="success-message" style={{ marginBottom: '1rem', padding: '0.75rem', background: '#d4edda', color: '#155724', borderRadius: '8px', border: '1px solid #c3e6cb' }}>
              ✓ Discussion guide has been sent to your email address!
            </div>
          )}
          {studyGuide ? (
            <div className="study-guide-content">
              <div className="study-guide-text">
                {studyGuide.guide_text
                  .split('\n')
                  .map((line, index) => {
                    const trimmedLine = line.trim();
                    
                    // Convert lines starting with ## to bold
                    if (trimmedLine.startsWith('##')) {
                      const text = trimmedLine.replace(/^##+\s*/, '');
                      // Process **text** within the line
                      const processedText = processBoldText(text);
                      return <p key={index} className="study-guide-paragraph"><strong>{processedText}</strong></p>;
                    }
                    
                    // Format headings (lines that start with #)
                    if (trimmedLine.startsWith('#')) {
                      const level = trimmedLine.match(/^#+/)?.[0].length || 1;
                      const text = trimmedLine.replace(/^#+\s*/, '');
                      // Process **text** within the heading
                      const processedText = processBoldText(text);
                      if (level === 1) {
                        return <h2 key={index} className="study-guide-heading">{processedText}</h2>;
                      } else if (level === 2) {
                        return <h3 key={index} className="study-guide-heading">{processedText}</h3>;
                      } else if (level === 3) {
                        return <h4 key={index} className="study-guide-heading">{processedText}</h4>;
                      } else {
                        return <h5 key={index} className="study-guide-heading">{processedText}</h5>;
                      }
                    }
                    
                    // Format bold text (lines that are short and might be headings)
                    if (trimmedLine.length > 0 && trimmedLine.length < 100 && !trimmedLine.includes('.') && trimmedLine === trimmedLine.toUpperCase() && trimmedLine.length > 3) {
                      const processedText = processBoldText(trimmedLine);
                      return <h3 key={index} className="study-guide-heading">{processedText}</h3>;
                    }
                    
                    // Regular paragraphs - process **text** for bold
                    if (trimmedLine.length > 0) {
                      const processedText = processBoldText(trimmedLine);
                      return <p key={index} className="study-guide-paragraph">{processedText}</p>;
                    }
                    
                    // Empty lines for spacing
                    return <br key={index} />;
                  })}
              </div>
            </div>
          ) : (
            <p>No discussion guide yet. Click "Generate Discussion Guide" above to create one!</p>
          )}
        </div>
      </div>
    </div>
  );
};

const QuestionAccordionItem = ({ question, answer, timestamp }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="accordion-item">
      <button
        className="accordion-header"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className="accordion-question">{question}</span>
        <span className="accordion-icon">{isOpen ? '−' : '+'}</span>
      </button>
      {isOpen && (
        <div className="accordion-content">
          <p className="accordion-answer">{answer}</p>
          {timestamp && (
            <p className="accordion-timestamp">
              Asked: {new Date(timestamp).toLocaleString()}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ summary, onRatingChange }) => {
  const [userRating, setUserRating] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUserRating();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary.id]);

  const fetchUserRating = async () => {
    try {
      const response = await axios.get(`${API_URL}/summaries/${summary.id}/rating`);
      setUserRating(response.data.rating);
    } catch (err) {
      console.error('Failed to fetch rating:', err);
    }
  };

  const handleRating = async (rating) => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/summaries/${summary.id}/rate`, { rating });
      setUserRating(rating);
      onRatingChange();
    } catch (err) {
      console.error('Failed to submit rating:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="summary-card">
      <div className="summary-header">
        <div>
          <p className="summary-author">By {summary.name || summary.email}</p>
          <p className="summary-date">
            {new Date(summary.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="summary-rating-info">
          {summary.average_rating > 0 && (
            <span>⭐ {parseFloat(summary.average_rating).toFixed(1)} ({summary.ratings_count} ratings)</span>
          )}
        </div>
      </div>
      <div className="summary-text">
        {summary.summary_text
          .split('\n')
          .map((line, index) => {
            const trimmedLine = line.trim();
            
            // Convert lines starting with ## to bold
            if (trimmedLine.startsWith('##')) {
              const text = trimmedLine.replace(/^##+\s*/, '');
              const processedText = processBoldText(text);
              return <p key={index}><strong>{processedText}</strong></p>;
            }
            
            // Regular paragraphs - process **text** for bold
            if (trimmedLine.length > 0) {
              const processedText = processBoldText(trimmedLine);
              return <p key={index}>{processedText}</p>;
            }
            
            // Empty lines for spacing
            return <br key={index} />;
          })}
      </div>
      <div className="summary-rating">
        <span>Rate this summary: </span>
        <StarRating
          rating={userRating}
          onRating={handleRating}
          disabled={submitting}
        />
      </div>
    </div>
  );
};

export default BookDetail;
